<template>
  <vcv-modal @close="changeDisplay">
    <template #header>
      <div class="row-block title">
        <h1>{{ title }}</h1>

        <vbutton
          v-if="isSharing === null && isTypeShareLink && sendingCandidates.length === 1"
          label="responses.copyLink"
          icon="icon_link"
          color="grey"
          text_color="grey-dark"
          :loading="share_loading"
          :disabled="share_loading"
          @click="copyLink"
        />
        <a ref="share_link" class="share-link" :href="share_link">{{ share_link }}</a>
      </div>
    </template>

    <template #body>
      <div class="modal-body">
        <div v-if="isSharing === null">
          <sending-candidates-block
            v-if="isTypeShareLink && sendingCandidates.length"
            :detete-from-share="deteteFromShare"
            :sending-candidates="sendingCandidates"
          />

          <div class="wrapper">
            <label>
              {{ $tr('responses.sendMessage.items.first.title') }}
              <transition name="fade">
                <span v-if="emailAlert" class="error">{{ emailAlert }}</span>
              </transition>
            </label>

            <div>
              <multiselect
                v-model="users"
                class="select-tags"
                track-by="id"
                name="emails"
                :options="options"
                :label="!is_japan_locale ? 'computed_name' : 'computed_name_reverse'"
                multiple
                taggable
                :show-labels="false"
                :allow-empty="false"
                :disabled="!isTypeShareLink && selectedResponses.length === 1"
                :placeholder="$tr('responses.selectRecipients')"
                @tag="addEmail"
              />
              <div v-if="isAddressDisabled" class="error-field">
                {{ $tr('responses.sendMessage.warnings.addressRequired') }}
              </div>
            </div>
          </div>

          <div v-if="apiMethod !== 'emailSend' && !isTypeShareLink">
            <label> {{ $tr('responses.sendMessage.items.templates.title') }}</label>
            <div>
              <multiselect
                v-model="selectedTemplate"
                :options="templates"
                :multiple="false"
                track-by="id"
                label="title"
                name="templates"
                :placeholder="$tr('responses.sendMessage.items.templates.title')"
                :show-labels="false"
                allow-empty
                class="select-tags"
              />
            </div>
          </div>

          <div class="wrapper">
            <label>{{ $tr('responses.sendMessage.items.second.title') }}</label>
            <div>
              <input
                v-model="subject"
                v-validate="'required'"
                type="text"
                :placeholder="$tr('responses.sendMessage.email')"
                name="subject"
                :class="{ error: errors.has('subject') }"
              />
            </div>
          </div>

          <div v-if="isEditorEnabled" class="wrapper">
            <label>
              <strong>{{ $tr('vacancy.emailTemplates.letterText') }}</strong>
            </label>
            <vcv-tiptap
              v-model="body"
              :is-empty="body === '' || body === '<p></p>'"
              :variables="isLinkShareShown ? share_vars : editor_vars"
              :toolbar="['bold', 'italic', 'list-unordered', 'list-ordered', 'link']"
              class="vcv-mass__tiptap"
            />
            <div v-if="errors.has('descr')" class="error">
              {{ $tr('common.requiredField') }}
            </div>
          </div>

          <div v-if="apiMethod !== 'emailSend'" class="row-block row-checkbox">
            <vchecklabel v-model="sendCopyMyself" :label="$tr('responses.sendCopyMyself')" />
          </div>

          <div v-if="alreadySentResponses && alreadySentResponses.length" class="row-block">
            <div class="send-again-header">
              <div class="send-again-title">
                <icon-warning class="send-again-icon" color="#000" />
                <span v-if="selectedAlreadySentResponses.length">{{
                  $tr('responses.sendMessage.warnings.alreadySent', {
                    count: selectedAlreadySentResponses.length,
                    candidate: pluralize($tr('responses.candidateSend'), selectedAlreadySentResponses.length),
                    candidateWhich: pluralize($tr('responses.candidateWhich'), selectedAlreadySentResponses.length)
                  })
                }}</span>
                <span v-else>{{ $tr('responses.sendMessage.warnings.onlyNew') }}</span>
              </div>
              <button class="send-again-button vcv-button transparent link" @click="toggleAlreadeSentUsersTable">
                {{
                  isAlreadeSentUsersTableVisible
                    ? $tr('responses.sendMessage.warnings.hide')
                    : $tr('responses.sendMessage.warnings.show')
                }}
              </button>
            </div>

            <div v-if="isAlreadeSentUsersTableVisible" class="send-again-body">
              <table class="send-again-table">
                <tr>
                  <th></th>
                  <th>Имя</th>
                  <th>E-mail</th>
                </tr>
                <tr v-for="res in alreadySentResponses" :key="res.id">
                  <td>
                    <input
                      :id="'already-sent-response-' + res.id"
                      v-model="selectedAlreadySentResponses"
                      type="checkbox"
                      :value="res"
                      class="checkbox"
                    />

                    <label class="already-sent-checkbox" :for="'already-sent-response-' + res.id"></label>
                  </td>
                  <td>{{ res.candidate.name + ' ' + res.candidate.surname }}</td>
                  <td>{{ res.candidate.email }}</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="result-wrapper">
        <div v-if="isSharing === true">
          {{ $tr('responses.sendMessage.result.progress') }}
        </div>

        <div v-if="isSharing === false">
          <div v-if="isShareSuccess">
            {{ $tr('responses.sendMessage.result.success') }}
          </div>
          <div v-else>
            {{ $tr('responses.sendMessage.result.error.first') }}<br />
            {{ $tr('responses.sendMessage.result.error.second') }}.
          </div>
        </div>
        <div v-if="isResponsesWithDifferentLanguages" class="red">
          {{ $tr('responses.sendMessage.warnings.differentLanguages') }}
        </div>
      </div>
    </template>

    <template v-if="isSharing === null" #footer>
      <div class="modal-footer">
        <vbutton label="responses.sendMessage.cancel" color="transparent" @click="changeDisplay" />
        <vbutton
          label="responses.sendMessage.button"
          :disabled="!valid || isAddressDisabled || isResponsesWithDifferentLanguages"
          @click="send"
        />
      </div>
    </template>
  </vcv-modal>
</template>

<script>
import axios from 'axios';
import { orderBy, uniqBy } from 'lodash';
import { setComputedName, compileTemplate, transformBR } from '@/_helpers/utils.js';
import Api from '@/api/response.js';
import Validator from 'validator';
import SendingCandidatesBlock from './SendingCandidatesBlock.vue';
import VcvTiptap from '@/components/VcvTiptap/VcvTiptap.vue';
import IconWarning from '@/components_new/svgs/icon-warning.vue';
import VcvModal from '@/components/VcvModal.vue';
import Pluralize from '@/mixins/pluralize';
import SafariBrowserDetector from '@/components/mixin/SafariBrowserDetector.js';
import { transformNL } from '@/_helpers/utils.js';

export default {
  name: 'SendMessageModal',

  components: {
    SendingCandidatesBlock,
    IconWarning,
    VcvModal,
    VcvTiptap
  },

  mixins: [SafariBrowserDetector, Pluralize],

  props: {
    title: {
      required: true,
      type: String
    },

    createSendData: {
      required: true,
      type: Function
    },

    apiMethod: {
      required: true,
      type: String
    },

    changeDisplay: {
      default: () => {},
      type: Function
    },

    selectedResponsesIds: {
      default: () => [],
      type: Array
    },

    selectedResponses: {
      default: () => [],
      type: Array
    },

    responses: {
      default: () => [],
      type: Array
    },

    emailTemplate: {
      default: () => {},
      type: Object
    },

    body_template: {
      default: null,
      type: Object
    },

    emailStatus: {
      default: 0,
      type: Number
    },

    initialBody: {
      type: String,
      default: ''
    },

    isSuccess: {
      type: Boolean,
      default: false
    },

    isGroup: {
      type: Boolean,
      default: false
    },

    isLinkShareShown: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isSharing: null,
      isShareSuccess: false,
      subject: '',
      body: '',
      recipients: [],
      emailAlert: null,
      tagsArray: [],
      emails: [],
      sentTemplatesArray: [],
      deletedCandidates: [],
      sendingCandidatesArray: [],
      selectedTemplate: null,
      isAlreadeSentUsersTableVisible: false,
      selectedAlreadySentResponses: [],
      isEditorEnabled: true,
      share_loading: false,
      copy_link: '',
      editor_vars: [
        { id: 'company_name', name: this.$tr('common.variables.company_name') },
        {
          id: 'vacancy_title',
          name: this.$tr('common.variables.vacancy_title')
        },
        {
          id: 'candidate_name',
          name: this.$tr('common.variables.candidate_name')
        },
        { id: 'full_name', name: this.$tr('common.variables.full_name') },
        {
          id: 'recruiter_firstname',
          name: this.$tr('common.variables.recruiter_firstname')
        },
        {
          id: 'recruiter_lastname',
          name: this.$tr('common.variables.recruiter_lastname')
        },
        {
          id: 'recruiter_phone',
          name: this.$tr('common.variables.recruiter_phone')
        },
        {
          id: 'recruiter_email',
          name: this.$tr('common.variables.recruiter_email')
        },
        {
          id: 'recruiter_position',
          name: this.$tr('common.variables.recruiter_position')
        },
        {
          id: 'interview_link',
          name: this.$tr('common.variables.interview_link')
        }
      ],

      share_vars: [
        { id: 'company_name', name: this.$tr('common.variables.company_name') },
        {
          id: 'vacancy_title',
          name: this.$tr('common.variables.vacancy_title')
        },
        {
          id: 'candidate_name',
          name: this.$tr('common.variables.candidate_name')
        },
        {
          id: 'recruiter_name',
          name: this.$tr('common.variables.recruiter_name')
        },
        {
          id: 'recruiter_phone',
          name: this.$tr('common.variables.recruiter_phone')
        },
        {
          id: 'recruiter_email',
          name: this.$tr('common.variables.recruiter_email')
        },
        {
          id: 'recruiter_position',
          name: this.$tr('common.variables.recruiter_position')
        },
        { id: 'link', name: this.$tr('common.variables.link') },
        {
          id: 'recipient_name',
          name: this.$tr('common.variables.recipient_name')
        },
        { id: 'comments', name: this.$tr('common.variables.comments') }
      ]
    };
  },

  computed: {
    slinkd() {
      if (!this.responses) {
        return '';
      }

      return +this.responses[0].status.id;
    },

    share_link() {
      return `${window.location.origin}/p/${this.copy_link}`;
    },

    is_sharing_modal() {
      return this.isSharing === null && this.isTypeShareLink && this.sendingCandidates.length === 1;
    },

    isAddressDisabled() {
      let isDisabled = true;
      if (!this.recipients.length) {
        return isDisabled;
      }

      if (this.alreadySentResponses.length && !this.concatRecipients.length) {
        return isDisabled;
      }
      isDisabled = false;

      return isDisabled;
    },

    isResponsesWithDifferentLanguages() {
      if (!this.selectedResponses || !this.selectedResponses.length || this.selectedResponses.length === 1) {
        return false;
      }

      if (this.isTypeShareLink) {
        return false;
      }

      let seen = new Set();
      const hasDuplicates = this.selectedResponses.some(function (response) {
        if (!response || !response.vacancy || !response.vacancy.language || !response.vacancy.language.code) {
          return true;
        }

        return seen.size === seen.add(response.vacancy.language.code).size;
      });

      return !hasDuplicates;
    },

    currentLocale() {
      return this.$store.state.locale.value;
    },

    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    },

    isTypeShareLink() {
      return this.apiMethod === 'shareLink';
    },

    valid() {
      return (
        Object.keys(this.fields)
          .map(k => this.fields[k])
          .every(f => f.valid === true) && this.recipients.length !== 0
      );
    },

    options() {
      let recipients = this.$store.getters['users/recipients'];

      if (this.isTypeShareLink) {
        const options = this.$store.getters['users/listDropdown'];

        const orderedOptions = orderBy(
          options,
          [option => (option.computed_name ? option.computed_name.toLowerCase() : '')],
          ['computed_name'],
          ['asc']
        );

        recipients =
          recipients && recipients.length
            ? recipients.map(recipient => {
                if (orderedOptions.find(o2 => recipient.email === o2.email)) {
                  const o2 = orderedOptions.find(o2 => recipient.email === o2.email);
                  return o2;
                }
                return recipient;
              })
            : [];

        const list = uniqBy([...recipients, ...orderedOptions], e => {
          return e.email;
        });

        return list;
      } else {
        const orderedOptions = orderBy(
          this.responses,
          [response => (response.computed_name ? response.computed_name.toLowerCase() : '')],
          ['computed_name'],
          ['asc']
        );

        recipients =
          recipients && recipients.length
            ? recipients.map(recipient => {
                if (orderedOptions.find(o2 => recipient.email === o2.email)) {
                  const o2 = orderedOptions.find(o2 => recipient.email === o2.email);
                  return o2;
                }
                return recipient;
              })
            : [];

        let list = uniqBy([...recipients, ...orderedOptions], e => {
          return e.email;
        });

        list = [...list, this.selectedResponses.map(x => ({ ...x, computed_name: `${x.name} ${x.surname}` }))];

        return list;
      }
    },

    users: {
      get() {
        if (!this.alreadySentResponses.length || this.isTypeShareLink) {
          return this.recipients;
        }

        const recipientsSelectedAlreadySent = this.getRecipientsSelectedAlreadySent(this.recipients);
        const recipientsUnsent = this.getUnsentRecipients(this.recipients);
        this.sendingCandidatesArray = [...recipientsSelectedAlreadySent, ...recipientsUnsent];
        // const sendingCandidates = this.sendingCandidates.map(x => ({ ...x, computed_name: `${x.name} ${x.surname}` }));

        return [...recipientsSelectedAlreadySent, ...recipientsUnsent, ...sendingCandidates];
      },

      async set(recipients) {
        const recipientsSelectedAlreadySent = this.getRecipientsSelectedAlreadySent(recipients);
        const recipientsUnsent = this.getUnsentRecipients(recipients);
        const filteredRecipients = [...recipientsSelectedAlreadySent, ...recipientsUnsent];
        this.sendingCandidatesArray = filteredRecipients.filter(v => v.pure !== true);

        if (!this.isTypeShareLink) {
          await this.$nextTick();
        }
        this.recipients = filteredRecipients.filter(v => v.pure !== true);
      }
    },

    recipientsVacancyIds() {
      if (!this.selectedResponses) {
        return [];
      }

      return this.selectedResponses.filter(x => x.vacancy !== undefined).map(response => response.vacancy.id);
    },

    isSameVacancy() {
      if (!this.recipientsVacancyIds) {
        return false;
      }

      if (this.recipientsVacancyIds.length === 1) {
        return true;
      }

      let id = null;
      let isSame = false;

      this.recipientsVacancyIds.forEach((item, index) => {
        if (index === 0) {
          id = item;
        } else {
          isSame = id === item;
        }
      });

      return isSame;
    },

    serverError() {
      return this.$store.state.response.sendMessageError;
    },

    sending() {
      return this.$store.state.response.sendingMessage;
    },

    sendingCandidates() {
      return this.responses
        .filter(i => this.selectedResponsesIds.includes(i.id))
        .filter(i => !this.deletedCandidates.includes(i.id))
        .map(item => {
          return {
            id: item.id,
            name: item.candidate.name,
            surname: item.candidate.surname,
            email: item.candidate.email
          };
        });
    },

    recipientsExtended() {
      const emailRecipients = this.recipients.filter(r => r.noUser);
      const emailRecipientsExtended = [];

      for (let recipient of emailRecipients) {
        for (let id of this.selectedResponsesIds) {
          emailRecipientsExtended.push(Object.assign({}, recipient, { id }));
        }
      }

      const responseRecipients = this.recipients.filter(r => !r.noUser);
      // responseRecipients.push(...emailRecipientsExtended)

      return responseRecipients;
    },

    userEmail() {
      return this.$store.getters['user/current'] ? this.$store.getters['user/current'].email : null;
    },

    templates() {
      const templatesOfCompany = this.getTemplatesOfCurrentLanguage(
        this.$store.getters['vacancy/emailTemplatesDefault']
      );
      const templatesOfVacancy = this.$store.getters['vacancy/emailTemplatesAll'];
      const templatesAll = templatesOfVacancy.length && this.isSameVacancy ? templatesOfVacancy : templatesOfCompany;
      const templatesWithTitle = templatesAll.map(item => {
        const responseStatusTitle = item.response_status ? item.response_status.title : '';
        const newItem = Object.assign({}, item, { title: item.title || responseStatusTitle });

        return newItem;
      });

      const templatesActive = this.isSameVacancy ? templatesWithTitle.filter(item => item.active) : templatesWithTitle;

      // console.log(templatesOfCompany);
      // console.log(templatesOfVacancy);
      // console.log(templatesAll);
      // console.log(templatesWithTitle);
      // console.log(templatesActive);

      return templatesActive;
    },

    sendCopyMyself: {
      get() {
        return this.users.some(r => {
          if (r.email) {
            return r.email === this.userEmail;
          } else if (r.candidate.email) {
            return r.candidate.email === this.userEmail;
          } else {
            return false;
          }
        });
      },

      set(val) {
        if (val) {
          this.addEmail(this.currentUser.email, this.currentUser);
        } else {
          this.users = this.users.filter(r => {
            if (r.email) {
              return r.email !== this.userEmail;
            } else if (r.candidate.email) {
              return r.candidate.email !== this.userEmail;
            } else {
              return false;
            }
          });
        }
      }
    },

    alreadySentResponses() {
      if (!this.sentTemplatesArray.length) {
        return [];
      }

      const filteredSentTemplatesArray = this.sentTemplatesArray.filter(item => {
        let hasRecipient = false;

        this.recipients.forEach(recipient => {
          if (recipient.id === item.id) {
            hasRecipient = true;
          }
        });
        return hasRecipient;
      });

      return filteredSentTemplatesArray;
    },

    concatRecipients() {
      if (!this.selectedResponses || !this.alreadySentResponses || !this.selectedAlreadySentResponses) {
        return [];
      }

      let notSentResponses = this.selectedResponses.filter(response => {
        let isCheck = false;

        this.alreadySentResponses.forEach(item => {
          if (!isCheck && response.id === item.id) {
            isCheck = true;
          }
        });

        return !isCheck;
      });

      return [...notSentResponses, ...this.selectedAlreadySentResponses];
    },

    currentUser() {
      return this.$store.getters['user/current'] ? this.$store.getters['user/current'] : null;
    },

    firstResponseVacancyTitle() {
      if (this.selectedResponses.length) {
        return this.selectedResponses[0].vacancy.name;
      } else if (this.emailTemplate) {
        return this.responses[0].vacancy.name;
      } else {
        return '';
      }
    },

    firstResponseCandidateName() {
      if (this.selectedResponses.length) {
        return {
          name: this.selectedResponses[0].candidate.name || '',
          surname: this.selectedResponses[0].candidate.surname || ''
        };
      } else if (this.emailTemplate) {
        return {
          name: this.responses[0].candidate.name || '',
          surname: this.responses[0].candidate.surname || ''
        };
      } else {
        return {
          name: '',
          surname: ''
        };
      }
    },

    company() {
      return this.$store.getters['company/one'];
    }
  },

  watch: {
    alreadySentResponses: {
      handler: function (value) {
        if (!value) {
          return;
        }

        if (this.selectedAlreadySentResponses && this.selectedAlreadySentResponses.length) {
          this.selectedAlreadySentResponses.splice(0, this.selectedAlreadySentResponses.length, ...value);
        } else {
          this.selectedAlreadySentResponses = value;
        }
      },

      immediate: true
    },

    emails(arr) {
      this.recipients = arr.map(item => {
        // TODO: Разобраться с noUser
        return {
          noUser: true,
          name: item,
          email: item
        };
      });
    },

    isSameVacancy: {
      handler: function (val) {
        if (val) {
          this.$store.dispatch('vacancy/getEmailTemplatesAll', {
            id: this.recipientsVacancyIds[0]
          });
        }
      },

      immediate: true
    },

    selectedTemplate(value) {
      if (value) {
        this.isEditorEnabled = false;
        this.subject = this.compileTemplate(value.template_header);
        this.body = this.compileTemplate(value.template_text);

        this.createSentTemplatesArray();

        if (!this.sendingCandidatesArray.length) {
          this.sendingCandidatesArray = [...this.recipients];
        }

        this.$nextTick().then(() => {
          this.isEditorEnabled = true;
        });
      }
    }
  },

  async created() {
    await this.fetchCompany();
    await this.$store.dispatch('users/fetchRecipients');

    if (!this.isTypeShareLink && this.options.length === 1) {
      this.users = this.options;
    }

    if (this.selectedResponsesIds && this.selectedResponsesIds.length && !this.isTypeShareLink) {
      // const filtered = this.responses.filter(i => this.selectedResponsesIds.includes(i.id));

      // this.recipients = this.recipients.concat(filtered);
      this.recipients = this.selectedResponses;
    }

    if (this.initialBody) {
      this.body = transformNL(this.initialBody);
    }

    const template = this.emailTemplate;
    if (template) {
      this.subject = this.compileTemplate(template.template_header);
      this.body = transformNL(this.compileTemplate(template.template_text));
    } else if (this.body_template) {
      this.subject = this.compileTemplate(this.body_template.subject);
      this.body = transformNL(this.compileTemplate(this.body_template.body));
    } else {
      this.subject = '';

      if (this.initialBody) {
        this.body = transformNL(this.initialBody);
      }
    }

    this.createSentTemplatesArray();

    if (this.isSuccess) {
      this.isSharing = false;
      this.isShareSuccess = true;
    }

    this.$store.dispatch('vacancy/getEmailTemplatesDefault');
  },

  methods: {
    fetchCompany() {
      this.$store.dispatch('company/fetch');
    },

    getRecipientsSelectedAlreadySent(recipients) {
      const filteredRecipients = recipients.filter(recipient => {
        let hasResponse = false;

        this.selectedAlreadySentResponses.forEach(response => {
          if (recipient.id === response.id) {
            hasResponse = true;
          }
        });

        return hasResponse === true;
      });

      return filteredRecipients;
    },

    getUnsentRecipients(recipients) {
      const notSentResponses = recipients.filter(response => {
        let isCheck = false;

        this.alreadySentResponses.forEach(item => {
          if (!isCheck && response.id === item.id) {
            isCheck = true;
          }
        });

        return !isCheck;
      });

      return notSentResponses;
    },

    compileTemplate(template) {
      if (this.isGroup) {
        return transformBR(template);
      }
      let compiledTemplate = template;

      let params = {
        isCompileVacancy: false,
        isCompileCandidate: false,
        data: {
          company: this.company,
          user: this.currentUser,
          vacancyTitle: this.firstResponseVacancyTitle,
          candidateName: this.firstResponseCandidateName
        },

        isTypeShareLink: this.isTypeShareLink,
        is_japan_locale: this.is_japan_locale
      };

      if (this.emailTemplate || this.selectedResponses.length === 1) {
        params.isCompileVacancy = true;
        params.isCompileCandidate = true;
      } else if (this.isSameVacancy) {
        params.isCompileVacancy = true;
        params.isCompileCandidate = false;
      }

      return compileTemplate(compiledTemplate, params, this.currentLocale);
    },

    createSentTemplatesArray() {
      if (this.isTypeShareLink) {
        return [];
      }

      if (this.emailTemplate) {
        const data = {
          responses: [this.selectedResponsesIds[0]],
          templates: [{ id: this.emailTemplate.id, is_default_template: false }]
        };

        Api['checkResponsesWithSentTemplate'](data).then(data => {
          const isSent = data[0].already_sent;

          if (isSent) {
            this.sentTemplatesArray.splice(0, this.sentTemplatesArray.length, ...this.responses);
          } else {
            this.sentTemplatesArray.splice(0, this.sentTemplatesArray.length);
          }
        });
      } else if (this.selectedTemplate && this.selectedResponses) {
        const templateId = this.selectedTemplate.id;
        const isDefaultTemplate = !this.isSameVacancy;

        const model = {
          responses: this.selectedResponsesIds,
          templates: [{ id: templateId, is_default_template: isDefaultTemplate }]
        };

        Api['checkResponsesWithSentTemplate'](model).then(data => {
          const alreadySentResponsesArray = this.selectedResponses.filter(response => {
            let isSent = false;
            data.forEach(template => {
              if (template.response_id === +response.id && template.already_sent) {
                isSent = true;
              }
            });

            return isSent;
          });

          this.sentTemplatesArray.splice(0, this.sentTemplatesArray.length, ...alreadySentResponsesArray);
        });
      } else {
        this.sentTemplatesArray.splice(0, this.sentTemplatesArray.length);
      }
    },

    toggleAlreadeSentUsersTable() {
      this.isAlreadeSentUsersTableVisible = !this.isAlreadeSentUsersTableVisible;
    },

    send(hasAlreadySent = false) {
      const isDefault = !this.isSameVacancy;

      if (this.apiMethod === 'emailSend') {
        if (this.isGroup) {
          const recipients = this.recipients;
          const data = recipients
            .filter(recipient => recipient.candidate)
            .map(item => {
              return {
                id: this.emailTemplate ? this.emailTemplate.id : null,
                response: {
                  id: item.id
                },
                template_header: this.subject,
                template_text: this.body,
                is_manual: true,
                is_default: false
              };
            });

          Api.emailSend(data)
            .then(() => {
              this.isSharing = false;
              this.isShareSuccess = true;
              this.$emit('submit');
            })
            .catch(() => {
              this.isSharing = false;
              this.isShareSuccess = false;
              this.$emit('submit');
            });
        } else {
          const data = [
            {
              id: this.emailTemplate ? this.emailTemplate.id : null,
              response: {
                id: this.responses[0].id
              },

              template_header: this.subject,
              template_text: this.body,
              is_manual: true,
              is_default: false
            }
          ];

          Api[this.apiMethod](data)
            .then(() => {
              this.isSharing = false;
              this.isShareSuccess = true;
              this.$emit('submit');
            })
            .catch(() => {
              this.isSharing = false;
              this.isShareSuccess = false;
              this.$emit('submit');
            });
        }

        return;
      }

      this.isSharing = true;

      if (this.selectedTemplate) {
        const recipients = hasAlreadySent ? this.sendingCandidatesArray : this.recipients;
        const data = recipients
          .filter(recipient => recipient.candidate)
          .map(item => {
            return {
              id: this.selectedTemplate.id,
              response: {
                id: item.id
              },
              copy_to_myself: this.sendCopyMyself,
              template_header: this.subject,
              template_text: this.body,
              is_manual: true,
              is_default: isDefault
            };
          });

        Api.emailSend(data)
          .then(() => {
            this.isSharing = false;
            this.isShareSuccess = true;
          })
          .catch(() => {
            this.isSharing = false;
            this.isShareSuccess = false;
          });
      } else {
        Api[this.apiMethod](this.createSendData(this))
          .then(() => {
            this.isSharing = false;
            this.isShareSuccess = true;
          })
          .catch(() => {
            this.isSharing = false;
            this.isShareSuccess = false;
          });
      }
    },

    addEmail(val, user = null) {
      const email = val ? val.trim() : '';
      if (!Validator.isEmail(email)) {
        this.flashEmailAlert(this.$tr('responses.sendMessage.warnings.wrongFormat'));
      } else if (this.recipients.map(v => v.email).indexOf(email) !== -1) {
        this.flashEmailAlert(this.$tr('responses.sendMessage.warnings.alreadyExists'));
      } else {
        const existUser = this.options.find(o => {
          const userEmail = o.email || (o.candidate ? o.candidate.email : '');
          return userEmail === email;
        });

        if (existUser) {
          this.recipients.push(existUser);
        } else {
          if (!this.isTypeShareLink && !user) {
            return;
          }
          this.recipients.push(
            Object.assign(
              {},
              setComputedName(
                user || {
                  name: email,
                  email
                }
              ),
              {
                noUser: true
              }
            )
          );
        }
      }
    },

    flashEmailAlert(message) {
      this.emailAlert = message;
      setTimeout(() => {
        this.emailAlert = null;
      }, 1000);
    },

    deteteFromShare(id) {
      this.deletedCandidates.push(id);
    },

    fallbackCopyTextToClipboard() {
      const share_link = this.$refs.share_link;
      let rng = document.createRange();
      rng.setStart(share_link, 0);
      rng.setEnd(share_link, 1);
      let sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(rng);
      try {
        document.execCommand('copy');
        window.getSelection().removeAllRanges();
        this.$noty.success(this.$tr('common.copy_link_success'));
      } catch (err) {
        console.log(err);
      }
    },

    copyLink() {
      if (!this.copy_link) {
        this.share_loading = true;
        axios
          .post('/1.0/response-links', {
            links: [
              {
                response: {
                  id: this.selectedResponsesIds[0]
                }
              }
            ]
          })
          .then(response => {
            const token =
              response && response.data && response.data.data && response.data.data[0]
                ? response.data.data[0].token
                : null;

            this.copy_link = token;
            setTimeout(() => {
              if (!navigator.clipboard) {
                this.fallbackCopyTextToClipboard();
              } else {
                const that = this;
                navigator.clipboard.writeText(this.share_link).then(
                  () => {
                    that.$noty.success(that.$tr('common.copy_link_success'));
                  },
                  err => {
                    console.error('Async: Could not copy text: ', err);
                  }
                );
              }
              this.share_loading = false;
            }, 100);

            if (window.gtag) {
              window.gtag({ event: 'response_share' });
            }
          })
          .catch(() => {
            this.share_loading = false;
          });
      } else {
        if (!navigator.clipboard) {
          this.fallbackCopyTextToClipboard();
        } else {
          const that = this;
          navigator.clipboard.writeText(this.share_link).then(
            () => {
              that.$noty.success(that.$tr('common.copy_link_success'));
            },
            err => {
              console.error('Async: Could not copy text: ', err);
            }
          );
        }

        if (window.gtag) {
          window.gtag({ event: 'response_share' });
        }
      }
    },

    getTemplatesOfCurrentLanguage(templates) {
      if (!templates) {
        return [];
      }

      const filteredTemplates = templates.filter(template => {
        if (!template.language) {
          return false;
        }

        if (
          !this.selectedResponses.length ||
          !this.selectedResponses[0].vacancy ||
          !this.selectedResponses[0].vacancy.language
        ) {
          return true;
        }

        const lang = this.selectedResponses[0].vacancy.language;

        return template.language.code === lang.code;
      });

      return filteredTemplates;
    }
  }
};
</script>

<style lang="scss" scoped>
input {
  width: 100%;
  padding: 10px 12px;
  line-height: 21px;
  font-size: 16px;
}

textarea {
  padding: 18px 16px;
  height: 313px;
  width: 652px;
  line-height: 18px;
  font-size: 14px;
  resize: none;
}

label {
  display: inline-block;
  margin-bottom: 3px;
}

.row-block.title {
  width: 100%;
  justify-content: space-between;

  h1 {
    line-height: 22px;
    font-size: 22px;
    font-weight: normal;
  }
}

.row-checkbox {
  display: flex;
}

.send-again-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;
  padding: 7px 10px;
  background: #f1f2f2;
  border-radius: 5px;
  font-size: 16px;
  line-height: 18px;
  box-sizing: border-box;
}

.send-again-title {
  display: flex;
  align-items: center;
}

.send-again-icon {
  margin-right: 10px;
}

.send-again-body {
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  padding: 12px;
  margin-top: 10px;
}

.send-again-table {
  th {
    font-size: 14px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.5);
    border-bottom: 1px solid #c4c4c4;
    text-align: left;
    padding-bottom: 5px;
  }

  tr {
    td {
      padding-top: 7px;
      padding-bottom: 7px;
      font-size: 16px;
      line-height: 24px;
    }
  }
}

.send-again-button {
  flex-shrink: 0;
  margin-left: 10px;
}

.red {
  color: $red-color !important;
  background-color: transparent !important;
  border-color: transparent !important;
}

.error-field {
  color: $red-color !important;
  background-color: transparent !important;
  border-color: transparent !important;
  font-size: 14px;
  line-height: 20px;
  margin-top: 7px;
}
.error {
  color: $red-color !important;
  background-color: transparent !important;
  border-color: transparent !important;
}

.already-sent-checkbox {
  margin: 0;
}

.share-link {
  width: 0;
  height: 0;
  z-index: -1;
  position: absolute;
  top: -9999px;
  left: -9999px;
}

.modal-body {
  padding: 0 25px;
}

.wrapper {
  margin-bottom: 10px;
}

.modal-footer {
  padding: 15px 25px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.result-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
